import { z } from "zod";

export const REVISIONED_DOMAIN_EVENT_V2_SCHEMA_VERSION = 2;
export const REVISIONED_DOMAIN_EVENT_SCHEMA_VERSION = 3;

export const FRONTEND_USER_ACTION_NAMES = [
  "accumulator.complete_setup",
  "accumulator.create",
  "agent.approval.decide",
  "agent.memory.create",
  "agent.memory.delete",
  "agent.memory.update",
  "agent.onboarding.start",
  "agent.portfolio_construction.start",
  "agent.prompt.submit",
  "agent.workspace.create",
  "agent.workspace.update",
  "auth.activate_synthetic_profile",
  "auth.clear_synthetic_profile",
  "auth.export_wallet",
  "auth.link_email",
  "auth.login",
  "auth.logout",
  "auth.update_email",
  "auth.update_synthetic_profile",
  "direct_index.update.submit",
  "funding.bank_transfer.instructions",
  "funding.bank_transfer.one_time",
  "funding.bank_transfer.verify",
  "investing_account.setup",
  "market_order.buy",
  "market_order.submit",
  "onboarding.enter",
  "onboarding.finish",
  "onboarding.portfolio.initialize",
  "onboarding.rebalance.run",
  "onboarding.schedule.start",
  "notification.archive",
  "notification.archive_read",
  "notification.read",
  "notification.read_all",
  "notification.unarchive",
  "operating_preferences.confirm",
  "portfolio.approval.decide",
  "portfolio.archive",
  "portfolio.archive_toggle",
  "portfolio.create",
  "portfolio.deposit.exchange_onramp_url",
  "portfolio.deposit.execute",
  "portfolio.deposit.glider_balance_transfer",
  "portfolio.deposit.stripe_ach_checkout",
  "portfolio.deposit.stripe_ach_link_auth",
  "portfolio.deposit.stripe_ach_session",
  "portfolio.deposit.stripe_onramp_session",
  "portfolio.deposit.track",
  "portfolio.rebalance.run",
  "portfolio.schedule.pause",
  "portfolio.schedule.resume",
  "portfolio.schedule.set_cadence",
  "portfolio.schedule.start",
  "portfolio.settings.save",
  "portfolio.swap_preferences.reset",
  "portfolio.swap_preferences.save",
  "portfolio.sell.execute",
  "portfolio.transfer.execute",
  "portfolio.unarchive",
  "portfolio.withdraw.execute",
  "receipt.share",
  "settings.display_preference.save",
  "settings.feature_code.redeem",
  "settings.oauth.drip.connect",
  "settings.oauth.drip.disconnect",
  "settings.oauth.twitter.connect",
  "settings.oauth.twitter.disconnect",
  "settings.swap_preferences.reset",
  "settings.swap_preferences.save",
  "settings.avatar.update",
  "settings.username.claim",
  "settings.username.rename",
  "start.construction.confirm",
  "start.draft.save",
  "start.onboarding.enter",
  "start.onboarding.finish",
  "start.portfolio.initialize",
  "start.profile.save",
  "start.rebalance.run",
  "start.schedule.start",
  "start.username.claim",
  "user_onboarding.avatar.save",
  "user_onboarding.profile.generate",
  "user_onboarding.profile.save",
  "ui_config.sync",
  "watchlist.add",
  "watchlist.remove",
  "watchlist.reorder",
] as const;
export const FrontendUserActionNameSchema = z.enum(FRONTEND_USER_ACTION_NAMES);
export type FrontendUserActionName = z.infer<
  typeof FrontendUserActionNameSchema
>;

export const FRONTEND_USER_ACTION_SURFACES = [
  "activity",
  "agent_control_plane",
  "asset",
  "app_shell",
  "auth",
  "dashboard",
  "direct_index_builder",
  "earn_accumulator",
  "funding",
  "investing_account",
  "onboarding",
  "portfolio",
  "portfolio_construction",
  "receipts",
  "settings",
  "start",
  "user_onboarding",
] as const;
export const FrontendUserActionSurfaceSchema = z.enum(
  FRONTEND_USER_ACTION_SURFACES,
);
export type FrontendUserActionSurface = z.infer<
  typeof FrontendUserActionSurfaceSchema
>;

export const DOMAIN_EVENT_NAMES = [
  "agent.onboarding.stage.reached",
  "agent.portfolio_construction.stage.reached",
  "direct_index_builder.analysis.completed",
  "direct_index_builder.analysis.requested",
  "direct_index_builder.checkout.opened",
  "execution.withdrawal.accepted",
  "execution.withdrawal.failed",
  "execution.withdrawal.requested",
  "frontend.auth.action.failed",
  "frontend.auth.action.requested",
  "frontend.auth.state.changed",
  "frontend.command_palette.opened",
  "frontend.command_palette.result.selected",
  "frontend.notification_center.opened",
  "frontend.route.viewed",
  "frontend.user_action.failed",
  "frontend.user_action.started",
  "frontend.user_action.succeeded",
  "platform_api.request.failed",
  "portfolio.activity.unclassified",
  "portfolio.deposit.finalized",
  "portfolio.deposit.observed",
  "portfolio.fee.finalized",
  "portfolio.fee.observed",
  "portfolio.fund_movement.finalized",
  "portfolio.fund_movement.observed",
  "portfolio.swap.finalized",
  "portfolio.swap.observed",
  "portfolio.transfer.finalized",
  "portfolio.transfer.observed",
  "portfolio.withdrawal.finalized",
  "portfolio.withdrawal.observed",
  "telemetry.provider.enabled",
] as const;

export const DomainEventNameSchema = z.enum(DOMAIN_EVENT_NAMES);
export type DomainEventName = z.infer<typeof DomainEventNameSchema>;

export const REVISIONED_DOMAIN_EVENT_SOURCES = [
  "indexer-cf",
  "indexer-cf-svm",
] as const;
export const RevisionedDomainEventSourceSchema = z.enum(
  REVISIONED_DOMAIN_EVENT_SOURCES,
);
export type RevisionedDomainEventSource = z.infer<
  typeof RevisionedDomainEventSourceSchema
>;

export const FRONTEND_DOMAIN_EVENT_NAMES = [
  "agent.onboarding.stage.reached",
  "agent.portfolio_construction.stage.reached",
  "direct_index_builder.analysis.completed",
  "direct_index_builder.analysis.requested",
  "direct_index_builder.checkout.opened",
  "execution.withdrawal.accepted",
  "execution.withdrawal.failed",
  "execution.withdrawal.requested",
  "frontend.auth.action.failed",
  "frontend.auth.action.requested",
  "frontend.auth.state.changed",
  "frontend.command_palette.opened",
  "frontend.command_palette.result.selected",
  "frontend.notification_center.opened",
  "frontend.route.viewed",
  "frontend.user_action.failed",
  "frontend.user_action.started",
  "frontend.user_action.succeeded",
  "platform_api.request.failed",
  "telemetry.provider.enabled",
] as const satisfies readonly DomainEventName[];

export const FrontendDomainEventNameSchema = z.enum(
  FRONTEND_DOMAIN_EVENT_NAMES,
);
export type FrontendDomainEventName = z.infer<
  typeof FrontendDomainEventNameSchema
>;

export const DomainEventOutcomeSchema = z.enum([
  "accepted",
  "cancelled",
  "delayed",
  "failure",
  "partial",
  "success",
]);
export type DomainEventOutcome = z.infer<typeof DomainEventOutcomeSchema>;

export const DomainEventChangeTypeSchema = z.enum(["upsert", "retract"]);
export type DomainEventChangeType = z.infer<typeof DomainEventChangeTypeSchema>;

export const DomainEventFinalitySchema = z.enum([
  "observed",
  "sequenced",
  "safe",
  "finalized",
  "orphaned",
]);
export type DomainEventFinality = z.infer<typeof DomainEventFinalitySchema>;

export const DomainErrorCodeSchema = z
  .string()
  .min(1)
  .max(128)
  .regex(
    /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/u,
    "Domain error codes must be lower_snake_case",
  );
export type DomainErrorCode = z.infer<typeof DomainErrorCodeSchema>;

export const DOMAIN_ERROR_CODES = {
  executionUnknown: "execution_unknown",
  platformApiClientError: "platform_api_client_error",
  platformApiNetworkError: "platform_api_network_error",
  platformApiServerError: "platform_api_server_error",
  platformApiTimeout: "platform_api_timeout",
} as const satisfies Readonly<Record<string, DomainErrorCode>>;

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue =
  | JsonPrimitive
  | readonly JsonValue[]
  | { readonly [key: string]: JsonValue };
export type JsonObject = { readonly [key: string]: JsonValue };

const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number().finite(),
    z.boolean(),
    z.null(),
    z.array(JsonValueSchema),
    z.record(z.string(), JsonValueSchema),
  ]),
);
export const JsonObjectSchema: z.ZodType<JsonObject> = z.record(
  z.string(),
  JsonValueSchema,
);
export const DOMAIN_EVENT_ID_MAX_LENGTH = 768;
export const DOMAIN_EVENT_IDENTIFIER_MAX_LENGTH = 512;

const RevisionedDomainEventCommonShape = {
  fact_type: z.literal("domain_event"),
  event_id: z.string().trim().min(1).max(DOMAIN_EVENT_ID_MAX_LENGTH),
  event_name: DomainEventNameSchema,
  occurred_at: z.iso.datetime({ offset: true }),
  emitted_at: z.iso.datetime({ offset: true }),
  environment: z.string().trim().min(1),
  outcome: DomainEventOutcomeSchema,
  error_code: DomainErrorCodeSchema.nullable(),
  provider_error_code: z.string().nullable(),
  sentry_event_id: z.string().nullable(),
  correlation_id: z.string().nullable(),
  causation_id: z.string().nullable(),
  request_id: z.string().nullable(),
  route_visit_id: z.string().nullable(),
  tenant_id: z.string().nullable(),
  api_key_id: z.string().nullable(),
  user_id: z.string().nullable(),
  owner_account_id: z.string().nullable(),
  portfolio_id: z.string().nullable(),
  strategy_id: z.string().nullable(),
  operation_id: z.string().nullable(),
  request_key: z.string().nullable(),
  chain_id: z.number().int().positive().nullable(),
  chain_ref: z.string().nullable(),
  route: z.string().nullable(),
  method: z.string().nullable(),
  details: JsonObjectSchema.nullable(),
} as const;

const RevisionedDomainEventVersionedShape = {
  ...RevisionedDomainEventCommonShape,
  event_source: RevisionedDomainEventSourceSchema,
  emitted_by: RevisionedDomainEventSourceSchema,
  logical_event_id: z
    .string()
    .trim()
    .min(1)
    .max(DOMAIN_EVENT_IDENTIFIER_MAX_LENGTH),
  change_type: DomainEventChangeTypeSchema,
  supersedes_event_id: z.string().trim().min(1).nullable(),
  payload_hash: z.string().regex(/^[0-9a-f]{64}$/u),
  decoder_version: z.string().trim().min(1).nullable(),
  reducer_version: z.string().trim().min(1),
  materializer_run_id: z.string().trim().min(1).nullable(),
  reindex_run_id: z.string().trim().min(1).nullable(),
  is_replay: z.boolean(),
  finality: DomainEventFinalitySchema,
  retraction_reason: DomainErrorCodeSchema.nullable(),
  block_hash: z.string().trim().min(1).nullable(),
  block_height: z.string().regex(/^\d+$/u).nullable(),
  tx_hash: z.string().trim().min(1).nullable(),
  log_index: z.number().int().nonnegative().nullable(),
  user_operation_hash: z.string().trim().min(1).nullable(),
  accounting_event_id: z.string().trim().min(1).nullable(),
  asset_id: z.string().trim().min(1).nullable(),
  amount_raw: z.string().regex(/^\d+$/u).nullable(),
} as const;

const RevisionedDomainEventActionDiscriminantsSchema = z
  .array(
    z
      .string()
      .trim()
      .min(1)
      .max(64)
      .regex(/^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/u),
  )
  .min(1)
  .optional();

const RevisionedDomainEventV2Shape = {
  schema_version: z.literal(REVISIONED_DOMAIN_EVENT_V2_SCHEMA_VERSION),
  ...RevisionedDomainEventVersionedShape,
} as const;

const RevisionedDomainEventV3Shape = {
  schema_version: z.literal(REVISIONED_DOMAIN_EVENT_SCHEMA_VERSION),
  ...RevisionedDomainEventVersionedShape,
  action_discriminants: RevisionedDomainEventActionDiscriminantsSchema,
} as const;

const validateRevisionLineage = (
  revision: {
    readonly change_type: DomainEventChangeType;
    readonly retraction_reason: DomainErrorCode | null;
    readonly supersedes_event_id: string | null;
  },
  context: z.core.$RefinementCtx,
): void => {
  if (
    revision.change_type === "retract" &&
    (revision.retraction_reason === null ||
      revision.supersedes_event_id === null)
  ) {
    context.addIssue({
      code: "custom",
      message:
        "Retractions require a reason and the superseded revision identity",
    });
  }
  if (
    revision.change_type === "upsert" &&
    revision.retraction_reason !== null
  ) {
    context.addIssue({
      code: "custom",
      message: "Upserts cannot carry a retraction reason",
    });
  }
};

export const RevisionedDomainEventDraftSchema = z
  .union([
    z.object(RevisionedDomainEventV2Shape).strict(),
    z.object(RevisionedDomainEventV3Shape).strict(),
  ])
  .superRefine(validateRevisionLineage);
export type RevisionedDomainEventDraft = z.infer<
  typeof RevisionedDomainEventDraftSchema
>;

export const RevisionedDomainEventFactSchema = z
  .union([
    z
      .object({
        ...RevisionedDomainEventV2Shape,
        source_generation: z.number().int().positive(),
      })
      .strict(),
    z
      .object({
        ...RevisionedDomainEventV3Shape,
        source_generation: z.number().int().positive(),
      })
      .strict(),
  ])
  .superRefine(validateRevisionLineage);
export type RevisionedDomainEventFact = z.infer<
  typeof RevisionedDomainEventFactSchema
>;

const canonicalizeJsonValue = (value: JsonValue): JsonValue => {
  if (Array.isArray(value)) {
    return value.map(canonicalizeJsonValue);
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, child]) => [key, canonicalizeJsonValue(child)]),
    );
  }
  return value;
};

export const canonicalJsonStringify = (value: JsonValue): string =>
  JSON.stringify(canonicalizeJsonValue(JsonValueSchema.parse(value)));

export const sha256Hex = async (value: string): Promise<string> => {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value),
  );
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

export const hashCanonicalDomainEventPayload = async (
  payload: JsonObject,
): Promise<string> => await sha256Hex(canonicalJsonStringify(payload));

export const createRevisionedDomainEventId = async (params: {
  readonly changeType: DomainEventChangeType;
  readonly logicalEventId: string;
  readonly payloadHash: string;
  readonly reducerVersion: string;
  readonly supersedesEventId?: string | null;
}): Promise<string> =>
  `evt_${await sha256Hex(
    [
      params.logicalEventId,
      params.reducerVersion,
      params.payloadHash,
      params.changeType,
      params.supersedesEventId ?? "",
    ].join("\u001f"),
  )}`;

export type RevisionedDomainEventInput = {
  readonly eventId: string;
  readonly logicalEventId: string;
  readonly eventName: DomainEventName;
  readonly occurredAt: string;
  readonly outcome: DomainEventOutcome;
  readonly changeType: DomainEventChangeType;
  readonly supersedesEventId?: string | null;
  readonly payloadHash: string;
  readonly decoderVersion?: string | null;
  readonly reducerVersion: string;
  readonly materializerRunId?: string | null;
  readonly reindexRunId?: string | null;
  readonly isReplay: boolean;
  readonly finality: DomainEventFinality;
  readonly retractionReason?: DomainErrorCode | null;
  readonly errorCode?: DomainErrorCode | null;
  readonly providerErrorCode?: string | null;
  readonly sentryEventId?: string | null;
  readonly correlationId?: string | null;
  readonly causationId?: string | null;
  readonly requestId?: string | null;
  readonly ownerAccountId?: string | null;
  readonly portfolioId?: string | null;
  readonly strategyId?: string | null;
  readonly operationId?: string | null;
  readonly requestKey?: string | null;
  readonly chainId?: number | null;
  readonly chainRef?: string | null;
  readonly blockHash?: string | null;
  readonly blockHeight?: string | null;
  readonly txHash?: string | null;
  readonly logIndex?: number | null;
  readonly userOperationHash?: string | null;
  readonly accountingEventId?: string | null;
  readonly assetId?: string | null;
  readonly amountRaw?: string | null;
  readonly actionDiscriminants?: readonly string[];
  readonly route?: string | null;
  readonly method?: string | null;
  readonly details?: JsonObject | null;
};

export const buildRevisionedDomainEventDraft = (params: {
  readonly environment: string;
  readonly event: RevisionedDomainEventInput;
  readonly now?: Date;
  readonly source?: RevisionedDomainEventSource;
}): RevisionedDomainEventDraft => {
  const event = params.event;
  const emittedAt = (params.now ?? new Date()).toISOString();
  const source = params.source ?? "indexer-cf";
  return RevisionedDomainEventDraftSchema.parse({
    schema_version: REVISIONED_DOMAIN_EVENT_SCHEMA_VERSION,
    fact_type: "domain_event",
    event_id: event.eventId,
    logical_event_id: event.logicalEventId,
    event_name: event.eventName,
    occurred_at: event.occurredAt,
    emitted_at: emittedAt,
    event_source: source,
    emitted_by: source,
    environment: params.environment,
    outcome: event.outcome,
    error_code: event.errorCode ?? null,
    provider_error_code: nullable(event.providerErrorCode),
    sentry_event_id: nullable(event.sentryEventId),
    correlation_id: nullable(event.correlationId),
    causation_id: nullable(event.causationId),
    request_id: nullable(event.requestId),
    route_visit_id: null,
    tenant_id: null,
    api_key_id: null,
    user_id: null,
    owner_account_id: nullable(event.ownerAccountId),
    portfolio_id: nullable(event.portfolioId),
    strategy_id: nullable(event.strategyId),
    operation_id: nullable(event.operationId),
    request_key: nullable(event.requestKey),
    chain_id: event.chainId ?? null,
    chain_ref: nullable(event.chainRef),
    change_type: event.changeType,
    supersedes_event_id: nullable(event.supersedesEventId),
    payload_hash: event.payloadHash,
    decoder_version: nullable(event.decoderVersion),
    reducer_version: event.reducerVersion,
    materializer_run_id: nullable(event.materializerRunId),
    reindex_run_id: nullable(event.reindexRunId),
    is_replay: event.isReplay,
    finality: event.finality,
    retraction_reason: event.retractionReason ?? null,
    block_hash: nullable(event.blockHash),
    block_height: nullable(event.blockHeight),
    tx_hash: nullable(event.txHash),
    log_index: event.logIndex ?? null,
    user_operation_hash: nullable(event.userOperationHash),
    accounting_event_id: nullable(event.accountingEventId),
    asset_id: nullable(event.assetId),
    amount_raw: nullable(event.amountRaw),
    ...(event.actionDiscriminants
      ? {
          action_discriminants: [...new Set(event.actionDiscriminants)].sort(
            (left, right) => left.localeCompare(right),
          ),
        }
      : {}),
    route: nullable(event.route),
    method: nullable(event.method),
    details: event.details ?? null,
  });
};

export const finalizeRevisionedDomainEventFact = (params: {
  readonly draft: RevisionedDomainEventDraft;
  readonly schemaVersion?:
    | typeof REVISIONED_DOMAIN_EVENT_V2_SCHEMA_VERSION
    | typeof REVISIONED_DOMAIN_EVENT_SCHEMA_VERSION;
  readonly sourceGeneration: number;
}): RevisionedDomainEventFact => {
  const schemaVersion = params.schemaVersion ?? params.draft.schema_version;
  if (
    schemaVersion === REVISIONED_DOMAIN_EVENT_V2_SCHEMA_VERSION &&
    params.draft.schema_version === REVISIONED_DOMAIN_EVENT_SCHEMA_VERSION
  ) {
    const v2Draft = { ...params.draft };
    delete v2Draft.action_discriminants;
    return RevisionedDomainEventFactSchema.parse({
      ...v2Draft,
      schema_version: REVISIONED_DOMAIN_EVENT_V2_SCHEMA_VERSION,
      source_generation: params.sourceGeneration,
    });
  }
  return RevisionedDomainEventFactSchema.parse({
    ...params.draft,
    schema_version: schemaVersion,
    source_generation: params.sourceGeneration,
  });
};

export type RevisionedDomainEventPipelineLike = {
  readonly send: (records: RevisionedDomainEventFact[]) => Promise<void>;
};

const nullable = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

export const createCorrelationId = (): string => `corr_${crypto.randomUUID()}`;
